import * as React from "react"
import * as TogglePrimitive from "@radix-ui/react-toggle"
import { cva, type VariantProps } from "class-variance-authority"

import { cn } from "@/lib/utils"

/**
 * 🔴 **L'état enclenché se peint sur les attributs ARIA, PAS sur `data-state`.**
 *
 * `data-[state=on]:…` est l'idiome shadcn d'origine, et il tombe dès qu'un
 * `<TooltipTrigger asChild>` enveloppe la bascule : Radix Tooltip pose SON
 * propre `data-state` (`closed` / `delayed-open`) sur le même élément, et il
 * écrase celui du Toggle. Aucune classe `data-[state=on]:*` ne s'applique plus
 * — la bascule paraît au repos alors qu'elle est active.
 *
 * Mesuré le 2026-08-22, après un retour PO sur le filtre « Devis sans activité »
 * du kanban : il était enclenché sans que rien ne le montre, si bien qu'on
 * pouvait chercher longtemps pourquoi des devis avaient disparu du tableau. Le
 * même motif touchait **13 bascules** — le sélecteur de densité des tableaux et
 * toute la barre d'outils de l'éditeur de documents (gras, italique, souligné,
 * listes, alignement, titres) : aucune ne montrait son état actif.
 *
 * `aria-pressed` (Toggle) et `aria-checked` (ToggleGroupItem) SURVIVENT à la
 * fusion : ils portent le sens, pas l'implémentation. `data-[state=on]` reste
 * là — il sert quand il n'y a pas de tooltip, et deux vérités concordantes ne
 * peuvent pas se contredire.
 *
 * Filet : `src/components/ui/__tests__/toggleStateUnderTooltip.test.tsx`.
 *
 * ─────────────────────────────────────────────────────────────────────────
 * 🔴 **L'état enclenché est REMPLI (`primary`), et non teinté (`accent`).**
 *
 * `--accent` vaut `162 46% 96%` en thème clair — 96 % de luminosité, c'est-à-dire
 * un blanc cassé. Posé sur un fond lui-même blanc, l'écart avec l'état au repos
 * était de quelques pour cent : deux options d'un sélecteur segmenté paraissaient
 * identiques, et l'utilisateur ne pouvait pas dire laquelle était choisie (retour
 * PO du 2026-09-01 sur « Complété par : Utilisateur / Intervenant »).
 *
 * Ce n'est pas un défaut de cet écran-là : `accent` sert de teinte de SURVOL
 * partout (`hover:bg-accent`), donc l'état CHOISI et l'état SURVOLÉ portaient la
 * même couleur. Un état permanent ne peut pas se peindre comme un état fugace.
 *
 * ⚠️ Le `hover:` de l'état enclenché est déclaré explicitement
 * (`…:hover:bg-primary/90`) : sans lui, `hover:bg-accent` — hérité de la variante
 * `outline` et de la base — pouvait reprendre la main selon l'ordre de sortie de
 * Tailwind, et le survol d'une option choisie la faisait paraître au repos.
 *
 * ⚠️ La couleur reste un renfort, jamais le seul signe : `font-semibold` et
 * `shadow-sm` accompagnent le remplissage (DS — jamais la couleur seule).
 */
const toggleVariants = cva(
  "inline-flex items-center justify-center rounded-md text-sm font-medium ring-offset-background transition-colors hover:bg-muted hover:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 data-[state=on]:bg-primary data-[state=on]:text-primary-foreground data-[state=on]:font-semibold data-[state=on]:shadow-sm data-[state=on]:hover:bg-primary/90 data-[state=on]:hover:text-primary-foreground aria-pressed:bg-primary aria-pressed:text-primary-foreground aria-pressed:font-semibold aria-pressed:shadow-sm aria-pressed:hover:bg-primary/90 aria-pressed:hover:text-primary-foreground aria-checked:bg-primary aria-checked:text-primary-foreground aria-checked:font-semibold aria-checked:shadow-sm aria-checked:hover:bg-primary/90 aria-checked:hover:text-primary-foreground [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0 gap-2",
  {
    variants: {
      variant: {
        default: "bg-transparent",
        outline:
          "border border-input bg-transparent hover:bg-accent hover:text-accent-foreground",
      },
      size: {
        default: "h-10 px-3 min-w-10",
        sm: "h-9 px-2.5 min-w-9",
        lg: "h-11 px-5 min-w-11",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  }
)

const Toggle = React.forwardRef<
  React.ElementRef<typeof TogglePrimitive.Root>,
  React.ComponentPropsWithoutRef<typeof TogglePrimitive.Root> &
    VariantProps<typeof toggleVariants>
>(({ className, variant, size, ...props }, ref) => (
  <TogglePrimitive.Root
    ref={ref}
    className={cn(toggleVariants({ variant, size, className }))}
    {...props}
  />
))

Toggle.displayName = TogglePrimitive.Root.displayName

// eslint-disable-next-line react-refresh/only-export-components
export { Toggle, toggleVariants }
